// Ask Hopscotch action dispatch — the FE side of the chat action protocol.
// The backend forwards tool calls as SSE `action` events ({ id, name, label,
// params, autoDispatch }); autoDispatch actions run immediately (add_game,
// today's behavior), the rest render as inline buttons and land here on
// click. Every branch fails soft: problems become an assistant-style note in
// the thread the action came from, never a silent no-op.

import { useGlobalState } from '@/stores/globalState'
import { queryClient } from '@/lib/queryClient'
import { fetchBookById, createGuideFromBook } from '@/lib/api/books'
import { mapLearnerStageToBackend } from '@/lib/stageMapping'
import { A2UI_ENABLED } from '@/lib/featureFlags'
import type { ChatMessage } from '@/types'

export interface CompanionActionInput {
  name: string
  params: Record<string, unknown>
}

function note(threadKey: string, content: string) {
  const msg: ChatMessage = {
    id: `companion-note-${Date.now()}`,
    role: 'assistant',
    content,
    timestamp: new Date(),
  }
  useGlobalState.getState().appendThreadMessage(threadKey, msg)
}

// ─── add_game (auto-dispatch) ────────────────────────────────────────────────
// Queue semantics lifted from the old RightPanel: actions arriving during an
// active build wait and dispatch when it finishes (drainQueuedAddGames is
// called from useCompanionChat's isGenerating watcher).

interface AddGameParams {
  guideId: string
  learnerId?: string
  category?: 'pattern_practice' | 'vocabulary' | 'fluency' | 'comprehension'
  focus?: string
}

const queuedAddGames: AddGameParams[] = []

function dispatchAddGame(params: AddGameParams) {
  const state = useGlobalState.getState()
  if (state.activeWorkspaceItem !== params.guideId) {
    console.warn('[companion] add_game dropped — user navigated away from the guide')
    return
  }
  if (state.isGenerating) {
    queuedAddGames.push(params)
    return
  }
  state.appendGameGeneration(params.guideId, {
    category: params.category,
    focus: params.focus,
  })
}

/** Called when a generation finishes — dispatches the next queued add_game. */
export function drainQueuedAddGames() {
  const params = queuedAddGames.shift()
  if (!params) return
  const state = useGlobalState.getState()
  if (state.activeWorkspaceItem !== params.guideId) {
    queuedAddGames.length = 0
    return
  }
  const remaining = queuedAddGames.length
  const label = params.category?.replace('_', ' ') ?? 'another'
  const suffix = remaining > 0 ? ` (${remaining} more in queue)` : ''
  note(params.guideId, `Now building the ${label} game you also requested…${suffix}`)
  state.appendGameGeneration(params.guideId, {
    category: params.category,
    focus: params.focus,
  })
}

// ─── create_set_from_book (button) ──────────────────────────────────────────

function dispatchCreateSet(params: Record<string, unknown>, threadKey: string) {
  const bookId = typeof params.bookId === 'string' ? params.bookId : null
  const state = useGlobalState.getState()
  const learner = state.activeLearner

  if (!bookId) {
    note(threadKey, "I couldn't tell which book that was — try asking again.")
    return
  }
  if (!learner) {
    note(threadKey, 'Pick a learner first (top left), then I can build the set.')
    return
  }
  if (state.isGenerating) {
    note(threadKey, 'A set is already building — let that finish, then tap the button again.')
    return
  }

  fetchBookById(bookId)
    .then((book) => {
      if (!book) {
        note(threadKey, "I couldn't find that book in the library anymore.")
        return
      }
      if (!book.mergedText) {
        note(
          threadKey,
          `"${book.title}" doesn't have its text ready yet — try again in a moment.`,
        )
        return
      }

      note(threadKey, `Starting a set of games from "${book.title}" — taking you to the canvas…`)

      const learnerStageBE = mapLearnerStageToBackend(learner.readingStage)
      return createGuideFromBook({
        bookId: book.id,
        title: book.title,
        learnerId: learner.id,
        learnerStageAtGen: learnerStageBE,
      }).then((guide) => {
        // Navigate NOW, before the stream produces anything — the Generating
        // wait screen (specs/generating-games-july9) owns all further
        // feedback: named rows, live progress, and the Ready-to-play / Print
        // completion state.
        const s = useGlobalState.getState()
        s.setActiveModule('MY_GUIDES')
        s.setActiveWorkspaceItem(guide.id)
        s.setWorkspaceView('GENERATING')

        s.streamGameGeneration(
          {
            raw_input_text: book.mergedText!,
            book_title: book.title,
            learner_stage: learnerStageBE,
            tenant_id: 'default',
            learner_id: learner.id,
            review_mode: 'auto',
            is_interactive: A2UI_ENABLED,
          },
          guide.id,
          () => {
            queryClient.invalidateQueries({ queryKey: ['reading-guides'] })
          },
        )
      })
    })
    .catch((err: unknown) => {
      const msg = err instanceof Error ? err.message : 'Something went wrong.'
      note(threadKey, `I couldn't start that set: ${msg}`)
    })
}

// ─── print_set (button) ──────────────────────────────────────────────────────

function dispatchPrintSet(params: Record<string, unknown>, threadKey: string) {
  const guideId = typeof params.guideId === 'string' ? params.guideId : null
  if (!guideId) {
    note(threadKey, 'Open a set first, then I can bring up the print view.')
    return
  }
  const state = useGlobalState.getState()
  // Navigate to the set's canvas if we're not already on it; CanvasView
  // watches printAllRequest and opens the batch print preview.
  if (state.activeWorkspaceItem !== guideId || state.workspaceView !== 'CANVAS') {
    state.setActiveModule('MY_GUIDES')
    state.setActiveWorkspaceItem(guideId)
    state.setWorkspaceView('CANVAS')
  }
  state.requestPrintAll(guideId)
}

// ─── Entry point ─────────────────────────────────────────────────────────────

export function dispatchCompanionAction(action: CompanionActionInput, threadKey: string) {
  switch (action.name) {
    case 'add_game':
      dispatchAddGame(action.params as unknown as AddGameParams)
      break
    case 'create_set_from_book':
      dispatchCreateSet(action.params, threadKey)
      break
    case 'print_set':
      dispatchPrintSet(action.params, threadKey)
      break
    default:
      console.warn('[companion] Unknown action:', action.name)
  }
}
